import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { api, ApiError } from '../api/client.js';

// The activation popup (licensing brief §10.2): one modal, two steps.
// Step 1 — paste a start.gg URL or slug; the server validates, registers
// (free), and returns the annotated event list. Step 2 — pick events
// (selectable = matching game + not completed; everything else says WHY),
// choose the stream from the tournament's registered channels, confirm the
// math, spend. Insufficient balance names the shortfall (run top-ups are
// admin-arranged until the checkout ships).
//
// Visual language follows ConfirmModal (the design-system modal): the
// confirm-modal-* shell for header/body/footer, btn-ghost for quiet
// actions, btn-pill for the money action, status-tag chips for the
// not-selectable reasons, and the .act-ev selectable rows.

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : null);

function statusChip(ev) {
  if (ev.status === 'completed') return { cls: 'status-tag--wait', text: 'Finished' };
  if (ev.status === 'wrong-game') return { cls: 'status-tag--work', text: `Needs ${ev.game ?? 'another game'}` };
  if (ev.status === 'active') return { cls: 'status-tag--ok', text: 'Activated' };
  return null;
}

export default function ActivationModal({ packageSlug, open, onClose, onActivated }) {
  const [step, setStep] = useState(1);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null); // { tournament, events, balance }
  const [picked, setPicked] = useState(() => new Set());
  const [streamName, setStreamName] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    setStep(1); setInput(''); setError(null); setData(null);
    setPicked(new Set()); setStreamName('');
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  async function lookup() {
    setBusy(true); setError(null);
    try {
      const res = await api.packageRegisterTournament(packageSlug, input);
      setData(res);
      setStreamName(res.tournament.streamName ?? res.tournament.streams?.[0] ?? '');
      setPicked(new Set());
      setStep(2);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Lookup failed');
    } finally {
      setBusy(false);
    }
  }

  async function activate() {
    setBusy(true); setError(null);
    try {
      const res = await api.packageActivate(packageSlug, {
        userTournamentId: data.tournament.id,
        eventIds: [...picked],
        streamName,
      });
      if (res?.ok) {
        onActivated?.(res);
        onClose();
      } else if (res?.reason === 'insufficient') {
        setError(`Not enough runs: this needs ${res.needed}, you have ${res.balance}. Ask your admin to top up.`);
      } else if (res?.reason === 'already-active') {
        setError('One of those events is already activated — refresh and try again.');
      } else if (res?.reason === 'event-completed') {
        setError(`"${res.eventName}" finished on start.gg since you opened this — it can't be activated.`);
      } else {
        setError(`Activation failed (${res?.reason ?? 'unknown'}).`);
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Activation failed');
    } finally {
      setBusy(false);
    }
  }

  // Insufficient balance turns the confirm button into checkout for the
  // shortfall (brief §10.2) — the tab redirects to the hosted payment page
  // and /billing/return lands the client back on the console.
  async function buyShortfall(n) {
    setBusy(true); setError(null);
    try {
      const res = await api.packageCheckout(packageSlug, n);
      if (res?.url) { window.location.assign(res.url); return; }
      setError('Checkout failed — try again.');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Checkout failed');
    }
    setBusy(false);
  }

  const t = data?.tournament;
  const selectable = (data?.events ?? []).filter((ev) => ev.status === 'activatable');
  const unselected = selectable.filter((ev) => !picked.has(ev.id));
  const balance = data?.balance ?? 0;
  const shortfall = picked.size - balance;
  const canConfirm = picked.size > 0 && shortfall <= 0 && !!streamName.trim() && !busy;

  return createPortal(
    <>
      <div className="confirm-modal-backdrop" onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-labelledby="act-modal-title" className="confirm-modal-layer" onClick={onClose}>
        <div
          className="confirm-modal-panel act-modal"
          style={{ '--cm-accent': 'var(--neon-blue)', '--cm-glow': 'var(--neon-blue-dim)', maxWidth: 560 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="confirm-modal-inner">
            <div className="confirm-modal-header">
              <h5 id="act-modal-title" className="confirm-modal-title">Activate a tournament</h5>
              <button type="button" className="confirm-modal-close" onClick={onClose} aria-label="Close">
                <X size={18} aria-hidden="true" />
              </button>
            </div>

            {step === 1 && (
              <>
                <div className="confirm-modal-body">
                  <label className="form-label" htmlFor="act-input">
                    start.gg tournament URL or slug
                  </label>
                  <input
                    id="act-input"
                    className="form-control"
                    value={input}
                    autoFocus
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && input.trim()) lookup(); }}
                    placeholder="https://www.start.gg/tournament/…"
                  />
                  <p className="act-modal__hint mt-2 mb-0">
                    Registering a tournament is free — activating its events spends runs.
                  </p>
                  {error && <p className="act-modal__error mt-2 mb-0">{error}</p>}
                </div>
                <div className="confirm-modal-footer">
                  <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
                  <button
                    type="button"
                    className="btn-pill btn-pill--primary"
                    disabled={!input.trim() || busy}
                    onClick={lookup}
                  >
                    {busy ? 'Looking up…' : 'Look up'}
                  </button>
                </div>
              </>
            )}

            {step === 2 && t && (
              <>
                <div className="confirm-modal-body">
                  <p className="act-modal__tname mb-1">{t.name}</p>
                  <p className="act-modal__hint mb-3">
                    {[fmtDate(t.startAt) && `${fmtDate(t.startAt)} – ${fmtDate(t.endAt) ?? '…'}`, t.city]
                      .filter(Boolean).join(' · ') || t.slug}
                  </p>

                  <div className="mb-3">
                    {(data.events ?? []).map((ev) => {
                      const selectableEv = ev.status === 'activatable';
                      const chip = statusChip(ev);
                      const checked = picked.has(ev.id);
                      return (
                        <label
                          className={`act-ev${checked ? ' is-checked' : ''}${selectableEv ? '' : ' is-disabled'}`}
                          key={ev.id}
                          htmlFor={`act-ev-${ev.id}`}
                        >
                          <input
                            id={`act-ev-${ev.id}`}
                            type="checkbox"
                            disabled={!selectableEv || busy}
                            checked={checked}
                            onChange={(e) => setPicked((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(ev.id); else next.delete(ev.id);
                              return next;
                            })}
                          />
                          <span className="act-ev__name">
                            {ev.name}
                            {ev.numEntrants != null && (
                              <span className="act-ev__meta"> · {ev.numEntrants} entrants</span>
                            )}
                          </span>
                          {chip && <span className={`status-tag ${chip.cls}`}>{chip.text}</span>}
                        </label>
                      );
                    })}
                  </div>

                  <label className="form-label" htmlFor="act-stream">Stream channel</label>
                  <input
                    id="act-stream"
                    className="form-control form-control-sm mb-1"
                    list="act-streams"
                    value={streamName}
                    onChange={(e) => setStreamName(e.target.value)}
                    placeholder={t.streams?.length ? 'pick a registered stream' : 'twitch channel name'}
                  />
                  <datalist id="act-streams">
                    {(t.streams ?? []).map((s) => <option value={s} key={s} />)}
                  </datalist>
                  <p className="act-modal__hint mb-0">
                    The channel your overlays watch — editable later, it never costs anything.
                  </p>

                  {unselected.length > 0 && picked.size > 0 && (
                    <p className="act-modal__warn mt-2 mb-0">
                      Not selected: {unselected.map((ev) => ev.name).join(', ')} — their sets
                      won&apos;t appear on your overlays.
                    </p>
                  )}
                  {error && <p className="act-modal__error mt-2 mb-0">{error}</p>}
                </div>

                <div className="confirm-modal-footer">
                  <span className="act-modal__math">
                    {picked.size} event{picked.size === 1 ? '' : 's'} × 1 run · balance {balance}
                    {shortfall > 0 && (
                      <strong className="act-modal__short"> — {shortfall} short</strong>
                    )}
                    <span className="d-block act-modal__hint">Free undo until an event&apos;s bracket starts.</span>
                  </span>
                  <button type="button" className="btn-ghost" disabled={busy} onClick={() => setStep(1)}>Back</button>
                  {shortfall > 0 && picked.size > 0 ? (
                    <button
                      type="button"
                      className="btn-pill btn-pill--primary"
                      disabled={busy}
                      onClick={() => buyShortfall(shortfall)}
                    >
                      {busy ? 'Redirecting…' : `Buy ${shortfall} more run${shortfall === 1 ? '' : 's'}`}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn-pill btn-pill--primary"
                      disabled={!canConfirm}
                      onClick={activate}
                    >
                      {busy ? 'Activating…' : `Activate ${picked.size || ''}`}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
